import * as React from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { Form, Button } from 'antd';
import intl from 'react-intl-universal';
import { get, isObject } from 'lodash';
import { useUpdate, useDebounceFn } from 'ahooks';

import { IsMobileCtx } from '../../context/isMobile';
import { teaTrack } from '@src/utils/tea';
import useTemplate from '../../model/getTemplate';
import { QuestionItem, TypeName } from '../../services/model';
import { submitRequest } from '../../services/submit';
import { QuestionnaireState } from '../../services/model';
import { path } from '../../Route';
import transferFormValue2SubmitData from '@src/apps/interview-questionnaire/utils/transferFormValue2SubmitData';
import { FormItem, scrollToview } from './FormItem';
import interpretQuestionRule from '../../utils/ruleInterpreter';

import './index.less';

const { useRef, useEffect, useContext, useState } = React;

/**
 * 移动端问卷详情内容组件
 * 负责渲染问卷表单、处理提交逻辑和表单验证
 */
const Content: React.FC = () => {
  // 获取移动端状态
  const { isMobile } = useContext(IsMobileCtx);
  // 提交加载状态
  const [loading, setLoading] = useState(false);
  // 获取问卷模板数据
  const { data } = useTemplate();
  // 获取问卷描述
  const description = get(data, 'questionnaire_desc', {});
  // 表单实例
  const [form] = Form.useForm();
  // 题目类型映射表
  const itemTypeMap = useRef<{ [key: string]: TypeName }>({});
  // 题目规则映射表
  const ruleMap = useRef<{ [key: string]: any }>({});
  // 从路由参数获取问卷token
  const { questionnaire_token } = useParams<{ questionnaire_token: string }>();
  // 错误信息列表
  const [errorList, setErrorList] = useState<{ [key: string]: string }>({});
  // 路由跳转方法
  const { replace } = useHistory();
  // 强制更新组件
  const update = useUpdate();
  // 防抖更新
  const { run: debouncedUpdate } = useDebounceFn(update, { wait: 500 });

  /**
   * 渲染问卷表单
   * 包含所有题目和提交按钮
   */
  const renderQuestionnaire = () => {
    // 数据未加载完成时不渲染
    if (!data) {
      return;
    }

    // 获取问题列表
    const question_list = (get(data, 'questionnaire_template_info.question_list', []) ??
      []) as QuestionItem[];

    return (
      <Form
        form={form}
        layout="vertical"
        className="mobile__detail__content__questionnaireDetail__form"
        onValuesChange={(field) => {
          // 重新校验当前修改的字段
          const key: string = Object.keys(field)[0];
          form.validateFields([key]);
          debouncedUpdate();
        }}
      >
        {/* 渲染所有问题项 */}
        {question_list?.map?.((item) => {
          // 根据规则判断题目是否显示
          const ruleCheckVisible = interpretQuestionRule(
            item?.rule,
            form.getFieldsValue(),
            itemTypeMap.current,
            ruleMap.current
          );

          return (
            <FormItem
              ruleCheckVisible={ruleCheckVisible}
              item={item}
              key={item.object_id}
              errorList={errorList}
              setErrorList={setErrorList}
              isChild={false}
            />
          );
        })}
      </Form>
    );
  };

  /**
   * 处理表单提交
   * 包含数据过滤、模板ID添加和提交逻辑
   */
  const handleSubmit = async () => {
    // 埋点：提交问卷
    teaTrack('atsx_satisfaction_questionnairee_submit', {
      tenant_id: window.tenant_id,
      channel: isMobile ? 'mobile' : 'PC'
    });

    try {
      setLoading(true);
      // 先校验表单
      const values = await form.validateFields();

      // 获取问题列表用于过滤
      const question_list = (get(data, 'questionnaire_template_info.question_list', []) ??
        []) as QuestionItem[];

      // 过滤隐藏字段
      const filteredValues = { ...values };
      const hide_question_ids: string[] = [];
      const newErrorList: { [key: string]: string } = {};

      // 检查每个字段是否应该被包含（根据显示规则）
      Object.keys(filteredValues).forEach((fieldKey) => {
        const question = question_list?.find((q) => q.object_id === fieldKey);
        if (question) {
          const isRuleVisible = interpretQuestionRule(
            question?.rule,
            filteredValues,
            itemTypeMap.current,
            ruleMap.current
          );

          // 如果字段是隐藏的，从提交数据中移除
          if (!isRuleVisible) {
            delete filteredValues[fieldKey];
            hide_question_ids.push(fieldKey);
          } else {
            // 校验必填说明
            const value = filteredValues[fieldKey];
            const config = get(question, 'type.config');
            const options = get(config, 'options', []) || [];
            const requireReasonKeys = options
              .filter((opt: any) => opt.require_reason)
              .map((opt: any) => opt.key);

            if (requireReasonKeys.length > 0) {
              // 单选
              if (Array.isArray(value) && value.length === 2 && typeof value[0] === 'string') {
                const [checked, reason] = value;
                if (requireReasonKeys.includes(checked) && !reason) {
                  newErrorList[fieldKey] = intl.get('pleaseSupplementExplanation');
                }
              }
              // 多选
              else if (Array.isArray(value) && value.length > 0 && Array.isArray(value[0])) {
                value.forEach(([checked, reason]: [string, string]) => {
                  if (requireReasonKeys.includes(checked) && !reason) {
                    newErrorList[fieldKey] = intl.get('pleaseSupplementExplanation');
                  }
                });
              }
            }
          }
        }
      });

      if (Object.keys(newErrorList).length > 0) {
        setErrorList(newErrorList);
        const id = Object.keys(newErrorList)[0];
        const elem = document.getElementById(id);
        scrollToview(elem);
        setLoading(false);
        return;
      }

      // 转换表单数据为提交格式
      const submitData = transferFormValue2SubmitData(
        filteredValues,
        itemTypeMap.current,
        questionnaire_token
      );

      // 安全获取 template_id
      const templateId =
        get(data, 'template_id') || get(data, 'questionnaire_template_info.template_id');
      if (isObject(submitData)) {
        if (templateId) {
          submitData.template_id = templateId;
        }
        submitData.hide_question_ids = hide_question_ids;
      }

      const { questionnaire_state, questionnaire_modify_tiome } = await submitRequest(submitData);
      setLoading(false);

      if (questionnaire_state === QuestionnaireState.FeedBack) {
        // 如果已经提交过，跳转到已提交页面
        teaTrack('atsx_satisfaction_questionnairee_filledpage', {
          tenant_id: window.tenant_id,
          channel: isMobile ? 'mobile' : 'PC'
        });
        replace(path.hasSubmitted(questionnaire_token), {
          last_submit_time: questionnaire_modify_tiome
        });
      } else if (questionnaire_state === QuestionnaireState.Invalid) {
        // 如果问卷失效，跳转到失效页面
        teaTrack('atsx_satisfaction_questionnairee_invalidationpage', {
          tenant_id: window.tenant_id,
          channel: isMobile ? 'mobile' : 'PC'
        });
        replace(path.expired(questionnaire_token));
      } else if (questionnaire_state === QuestionnaireState.Sent) {
        // 如果提交成功，跳转到成功页面
        teaTrack('atsx_satisfaction_questionnairee_submit_success', {
          tenant_id: window.tenant_id,
          channel: isMobile ? 'mobile' : 'PC'
        });
        replace(path.success(questionnaire_token));
      }
    } catch (error) {
      // 处理表单校验错误
      const id = get(error, 'errorFields.0.name', '');
      const elem = document.getElementById(id);
      const errorFields = get(error, 'errorFields', []);

      // 更新错误列表状态
      setErrorList(() => {
        return errorFields.reduce((res: any, item: any) => {
          res[get(item, 'name.0')] = get(item, 'errors.0');
          return res;
        }, {});
      });

      // 滚动到错误元素
      scrollToview(elem);
      setLoading(false);
    }
  };
  /**
   * 处理问卷数据加载完成后的初始化
   * 构建题目类型映射表用于后续处理
   */
  useEffect(() => {
    if (!data) return;

    const question_list = (get(data, 'questionnaire_template_info.question_list', []) ??
      []) as QuestionItem[];

    // 构建题目类型映射表（包含主题目和子题目）
    const newItemTypeMap = question_list.reduce((data, item) => {
      const typeName = item.type.type;
      const id = item.object_id;
      const childList = (get(item, 'children_list', []) ?? []) as QuestionItem[];

      // 如果有子题目，也添加到映射表中
      if (childList?.length > 0) {
        const childTypeMap = childList?.reduce((data, item) => {
          const child_typeName = item.type.type;
          const child_id = item.object_id;
          return { ...data, [child_id]: child_typeName };
        }, {});
        return { ...data, [id]: typeName, ...childTypeMap };
      }
      return { ...data, [id]: typeName };
    }, {});

    // 构建题目规则映射表（包含主题目和子题目）
    const newRuleMap = question_list.reduce((data, item) => {
      const rule = item.rule;
      const id = item.object_id;
      const childList = (get(item, 'children_list', []) ?? []) as QuestionItem[];

      // 如果有子题目，也添加到映射表中
      if (childList?.length > 0) {
        const childRuleMap = childList?.reduce((data, item) => {
          const child_rule = item.rule;
          const child_id = item.object_id;
          return { ...data, [child_id]: child_rule };
        }, {});
        return { ...data, [id]: rule, ...childRuleMap };
      }
      return { ...data, [id]: rule };
    }, {});

    itemTypeMap.current = newItemTypeMap;
    ruleMap.current = newRuleMap;
  }, [data]);

  /**
   * 处理页面刷新提示
   * 当表单有数据时提醒用户
   */
  useEffect(() => {
    window.onbeforeunload = () => {
      const values = form.getFieldsValue();
      return Object.keys(values).every((item) => !item) ? '' : 'alert';
    };
    return () => {
      window.onbeforeunload = null;
    };
  }, []);

  /**
   * 渲染移动端问卷组件
   * 包含问卷内容和提交按钮
   */
  return (
    <div className="mobile-detail-content-wrapper">
      {/* 问卷描述信息 */}
      {description?.i18n_name && (
        <div className="questionnaire-description-mobile">{description?.i18n_name}</div>
      )}

      {/* 问卷内容区域 */}
      <div className="mobil-detail-content">{renderQuestionnaire()}</div>

      {/* 底部提交按钮 */}
      <div className="mobile-detail-footer">
        <Button type="primary" onClick={handleSubmit} loading={loading}>
          {intl.get('submit')}
        </Button>
      </div>
    </div>
  );
};

export default Content;
